"use client";

import {
  AdaptiveDrawer,
  AdaptiveDrawerTrigger,
  AdaptiveDrawerContent,
  AdaptiveDrawerDescription,
  AdaptiveDrawerHeader,
  AdaptiveDrawerTitle,
} from "@/components/ui/adaptive-drawer";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { PutToCartButton } from "@/app/(shop)/shop/produkte/[title]/components/put-to-cart-button";
import { AVAILABILITY } from "@/app/(shop)/shop/produkte/[title]/components/availability-display";
import { Separator } from "@/components/ui/separator";
import { ProductPriceDisplay } from "@/app/(shop)/shop/produkte/[title]/components/price-display";

type ProductOption = {
  id: string;
  name: string;
  values: {
    id: string;
    value: string;
    label: string;
  }[];
};
export const AddToCartButton = ({
  id,
  options,
  variants,
  selected,
}: {
  id: string;
  options: ProductOption[];
  variants: {
    id: string;
    isAvailable?: number;
    isDefault?: boolean;
    price?: number;
    strikePrice?: number;
    vat?: number;
    optionValues: {
      id: string;
      label: string;
      value: string;
      optionId: string;
      name: string;
    }[];
  }[];
  selected: { option: string; value: string }[];
}) => {
  const [open, setOpen] = useState(false);
  const [currentVariant, setCurrentVariant] = useState<{
    id: string;
    isAvailable?: number;
    isDefault?: boolean;
    price?: number;
    strikePrice?: number;
    vat?: number;
    optionValues: {
      id: string;
      label: string;
      value: string;
      optionId: string;
      name: string;
    }[];
  } | null>(variants.find((variant) => variant.isDefault) ?? null);

  const currentOptionValues = currentVariant?.optionValues;

  const canBeAddedToCart = !!currentVariant?.price;
  const availability = canBeAddedToCart
    ? currentVariant.isAvailable
      ? AVAILABILITY.AVAILABLE
      : AVAILABILITY.NOT_AVAILABLE
    : AVAILABILITY.NEEDS_SELECTION;

  return (
    <AdaptiveDrawer
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
      }}
      onClose={() => {
        setOpen(false);
      }}
      shouldScaleBackground
    >
      <AdaptiveDrawerTrigger asChild>
        <Button variant="outline">
          <PlusIcon className="size-4 text-orange-600" />
        </Button>
      </AdaptiveDrawerTrigger>
      <AdaptiveDrawerContent className="">
        <div className="max-h-[90vh] grow overflow-y-auto pb-12">
          <div className="flex flex-col px-6">
            <AdaptiveDrawerHeader className="">
              <AdaptiveDrawerTitle>
                Zum Warenkorb hinzufügen
              </AdaptiveDrawerTitle>
              <AdaptiveDrawerDescription asChild>
                <div aria-description="Variantenauswahl">
                  {variants?.length > 1 ? (
                    <p>
                      Bitte wähle eine Variante und Menge aus, um das Produkt in
                      den Warenkorb zu legen.
                    </p>
                  ) : (
                    <p>
                      Wähle die Menge aus, um das Produkt in den Warenkorb zu
                      legen.
                    </p>
                  )}
                </div>
              </AdaptiveDrawerDescription>
            </AdaptiveDrawerHeader>
            <Options
              options={options ?? []}
              available={variants}
              selected={selected}
              onSelect={(variant) => {
                setCurrentVariant(variant);
              }}
            />

            <Separator className="my-4" />

            <div className="my-2 pt-4">
              <h3 className="pb-1 font-semibold">Preis</h3>
              <ProductPriceDisplay
                availability={availability}
                price={currentVariant?.price}
                strikePrice={currentVariant?.strikePrice}
                taxRate={currentVariant?.vat}
                serviceAddons={[]}
              />
            </div>

            <PutToCartButton
              variantId={currentVariant?.id}
              availability={availability}
              isDefaultVariant={currentVariant?.isDefault}
              serviceAddons={[]}
              currentPrice={currentVariant?.price}
              onClick={() => {
                setOpen(false);
              }}
            />
          </div>
        </div>
      </AdaptiveDrawerContent>
    </AdaptiveDrawer>
  );
};

function Options({
  options,
  available,
  selected,
  onSelect,
}: {
  options: {
    id: string;
    name: string;
    values: {
      id: string;
      value: string;
      label: string;
    }[];
  }[];
  selected?: {
    option: string;
    value: string;
  }[];
  available: {
    id: string;
    isAvailable?: number;
    price?: number;
    optionValues: {
      id: string;
      label: string;
      value: string;
      optionId: string;
      name: string;
    }[];
  }[];
  onSelect: (variant: (typeof available)[0] | null) => void;
}) {
  const [selectedValues, setSelectedValues] = useState<{
    [key: string]: string;
  }>(selected?.reduce((acc, d) => ({ ...acc, [d.option]: d.value }), {}) ?? {});

  const selectedOptions = options.map((option) => {
    return {
      optionId: option.id,
      option: option.name,
      value:
        selectedValues[option.name],
    };
  });

  const selectedOptionsLength = Object.keys(selectedValues).length;

  // Find matching variant whenever selected values change
  useEffect(() => {
    const selectedVariant = available.find((variant) => {
      return selectedOptions.every((selectedOption) => {
        if (!selectedOption.value) return false;
        return variant.optionValues.find((ov) => {
          return (
            ov.optionId === selectedOption.optionId &&
            ov.value === selectedOption.value
          );
        });
      });
    });
    onSelect(selectedVariant ?? null);
  }, [selectedValues, available, selectedOptions, onSelect]);

  return (
    <section className="flex flex-col gap-2">
      {options.map((option, idx) => {
        return (
          <div key={option.id}>
            <h3 className="pb-1 font-semibold">{option.name}</h3>
            <ul className="flex flex-row flex-wrap items-center justify-start gap-2">
              {option.values.map((value) => {
                const prevOptions = selectedOptions.slice(0, idx);

                const possibleVariant = [
                  ...prevOptions,
                  {
                    optionId: option.id,
                    option: option.name,
                    value: value.value,
                  },
                ];
                const availableVariant = available?.find((variant) => {
                  return possibleVariant.every((selectedOption) => {
                    return variant.optionValues.find((ov) => {
                      return (
                        ov.optionId === selectedOption.optionId &&
                        ov.value === selectedOption.value
                      );
                    });
                  });
                });

                const isAvailable = !!availableVariant?.isAvailable;
                const exists = !!availableVariant;

                const isSelected = selectedValues[option.name] === value.value;

                return (
                  <OptionValue
                    key={value.id}
                    option={option}
                    value={value}
                    optionIndex={idx}
                    totalOptions={options.length}
                    selectedOptionsLength={selectedOptionsLength}
                    isSelected={isSelected}
                    exists={exists}
                    onOptionChange={(option, value) => {
                      // deselect all options after this one
                      const nextOptions = options.slice(idx + 1);
                      const remainingValues = { ...selectedValues };

                      // Remove values for options after current one
                      nextOptions.forEach((opt) => {
                        delete remainingValues[opt.name];
                      });

                      if (value === null) {
                        delete remainingValues[option];
                      } else {
                        remainingValues[option] = value;
                      }

                      setSelectedValues(remainingValues);
                    }}
                    available={isAvailable}
                  />
                );
              })}
            </ul>
          </div>
        );
      })}
    </section>
  );
}

const OptionValue = ({
  option,
  value,
  available,
  optionIndex,
  totalOptions,
  onOptionChange,
  selectedOptionsLength,
  isSelected,
  exists = false,
}: {
  option: { name: string };
  value: { id: string; value: string; label: string };
  available?: boolean;
  onOptionChange?: (option: string, value: string | null) => void;
  totalOptions: number;
  optionIndex: number;
  selectedOptionsLength: number;
  isSelected: boolean;
  exists?: boolean;
}) => {
  const isDisabled = selectedOptionsLength < optionIndex && !isSelected;

  return (
    <Button
      variant={"outline"}
      size={"lg"}
      disabled={isDisabled || !exists}
      className={
        isSelected
          ? "border border-orange-300 bg-orange-50/75 hover:bg-orange-200"
          : ""
      }
      onClick={() => {
        if (!isSelected) {
          onOptionChange?.(option.name, value.value);
        } else {
          onOptionChange?.(option.name, null);
        }
      }}
    >
      {value.label}
    </Button>
  );
};
